import React, { useState } from 'react';
import dayjs from 'utils/dayjs';
import { useSelector } from 'react-redux';
import { merge } from 'lodash';
import { CSVLink } from 'react-csv';

import session from 'containers/App/session';
import { FEATURES } from 'utils/permissions';
import { parseBusinessReviewData } from './parser';
import { transformDataForGrid, transformDataForCSV, groupRows, formatData } from './businessReviewUtils';
import { getCalendarColumnDefinitions } from './calendarGridUtils';
import { getDatesArrayForRange } from '../../../helpers/dateHelper';

import Spinner from '../../UI/spinner';
import Grid from './grid';
import LabelCellRenderer from './labelCellRenderer';

import './businessReview.style.css';

export const BusinessReview = ({
    currency,
    startDate,
    endDate,
    currentData,
    budget,
    forecast,
    statistics,
    inventoryBlocks,
    sellingRate,
    defaultRoomType,
    dayClicked,
    options,
}) => {
    const [subRowsVisibility, setSubRowsVisibility] = useState({
        'rooms.header': false,
        'revenue.header': false,
        'forecast.revenue': false,
        'forecast.rooms': false,
        'budget.revenue': false,
    });
    const accessibleFeatures = useSelector(session.selectors.selectAccessibleFeatures);
    const displayDateFormat = 'ddd DD MMM YY';
    const displayDateFormatNoDay = 'DD MMM YY';
    const todayColId = 'today';
    const frameworkComponents = { labelCellRenderer: LabelCellRenderer };
    const rowsGrouping = [
        {
            parentKey: 'rooms.header',
            childrenKeys: [
                'rooms.guaranteed',
                'rooms.nonGuaranteed',
                'rooms.tentative',
                'invBlock.rooms',
                'rooms.noShow',
                'rooms.cancelled',
                'rooms.totalAllStatuses',
                'rooms.ooo',
            ],
        },
        {
            parentKey: 'revenue.header',
            childrenKeys: [
                'revenue.guaranteed',
                'revenue.nonGuaranteed',
                'revenue.tentative',
                'invBlock.revenue',
                'revenue.noShow',
                'revenue.cancelled',
                'revenue.fb',
                'revenue.ancillary',
                'revenue.totalAllStatuses',
                'revenue.other',
                'statistics.revenue.accommodation',
            ],
        },
        {
            parentKey: 'budget.revenue',
            childrenKeys: [
                'budget.revenue.variance',
                'budget.rooms',
                'budget.rooms.variance',
                'budget.occupancy',
                'budget.occupancy.variance',
                'budget.adr',
                'budget.adr.variance',
                'budget.revPar',
                'budget.revPar.variance',
            ],
        },
    ];
    // Rows at the bottom of which a dividing line will be drawn
    const dividingRows = [];

    const onClickDayColumnHeaderHandler = (val) => {
        if (!val.title.toLowerCase().includes('total')) {
            const date = dayjs(val.title.substring(4, val.title.length), displayDateFormatNoDay);
            if (dayClicked) {
                dayClicked(date);
            }
        }
    };

    const onClickGroupingLabelCellHandler = (key) => {
        setSubRowsVisibility({
            ...subRowsVisibility,
            [key]: !subRowsVisibility[key],
        });
    };

    const getNodeChildDetails = (rowItem) => {
        if (!rowItem.children) return null;
        return {
            rowGroup: true,
            expanded: rowItem.expanded,
            children: rowItem.children,
            key: rowItem.rowLabel,
        };
    };

    const getRowClass = (params) => {
        const { key } = params.data;
        if (dividingRows.includes(key)) return 'BRRowDivider';
        if (rowsGrouping.find((row) => row.childrenKeys.includes(key))) return 'BRSubRow';
        return null;
    };

    // Columns definition for the Grid component
    const getGridColumnDefinitions = (op) => {
        const columnExtras = {
            // headerComponentFramework: ClickableHeader,
            headerComponentParams: {
                headerClicked: op.clickableColumnHeader ? onClickDayColumnHeaderHandler : null,
            },
        };
        const monthData = getCalendarColumnDefinitions(
            getDatesArrayForRange(startDate, endDate),
            todayColId,
            displayDateFormat,
            columnExtras,
        );
        const totalCellStyle = { cellStyle: { textAlign: 'right' } };

        return [
            {
                headerName: '',
                field: 'label',
                pinned: 'left',
                cellClass: 'BRCellBold BRCellCalendar',
                width: 210,
                cellRenderer: LabelCellRenderer,
                fixed: 'left',
                showHeader: false,
                title: '',
                align: 'left',
            },
            ...monthData,
            {
                headerName: 'Total',
                field: 'total',
                pinned: 'right',
                ...totalCellStyle,
                cellClass: 'BRCellBold BRCellCalendar',
                width: 100,
                fixed: 'right',
                title: 'Total',
                align: 'right',
            },
        ];
    };

    const renderNoData = () => <div className="BusinessReviewNoData">No existing data for selected date range.</div>;

    if (!currentData) return <Spinner />;
    // eslint-disable-next-line no-param-reassign
    options = merge(
        {},
        {
            clickableColumnHeader: true,
            showSellingRate: true,
            showBlocks: true,
        },
        options,
    );

    const data = parseBusinessReviewData(
        currentData,
        budget,
        forecast,
        statistics,
        sellingRate,
        defaultRoomType,
        inventoryBlocks,
    );
    const excludedRows = [];

    // Add/delete forecast rows depending on permissions
    const canViewRevenueForecast = accessibleFeatures.includes(FEATURES.revenueForecast);
    const canViewRoomForecast = accessibleFeatures.includes(FEATURES.roomForecast);
    const canViewPMSBalancing = accessibleFeatures.includes(FEATURES.pmsBalancing);
    const canViewSellingRate = options.showSellingRate && (accessibleFeatures.includes(FEATURES.cardinalRecommendations) || accessibleFeatures.includes(FEATURES.viewAERecs));

    if (canViewRevenueForecast) {
        const forecastGrouping = {
            parentKey: 'forecast.revenue',
            childrenKeys: [
                'forecast.revenue.variance',
                'forecast.adr',
                'forecast.adr.variance',
                'forecast.revPar',
                'forecast.revPar.variance',
            ],
        };
        if (canViewRoomForecast) {
            forecastGrouping.childrenKeys.push('forecast.rooms');
            forecastGrouping.childrenKeys.push('forecast.rooms.variance');
            forecastGrouping.childrenKeys.push('forecast.occupancy');
            forecastGrouping.childrenKeys.push('forecast.occupancy.variance');
        }
        rowsGrouping.push(forecastGrouping);
    } else if (canViewRoomForecast) {
        rowsGrouping.push({
            parentKey: 'forecast.rooms',
            childrenKeys: ['forecast.rooms.variance', 'forecast.occupancy', 'forecast.occupancy.variance'],
        });
    }
    if (!canViewRevenueForecast) {
        excludedRows.push('forecast.revenue');
        excludedRows.push('forecast.revenue.variance');
        excludedRows.push('forecast.adr');
        excludedRows.push('forecast.adr.variance');
        excludedRows.push('forecast.revPar');
        excludedRows.push('forecast.revPar.variance');
    }
    if (!canViewRoomForecast) {
        excludedRows.push('forecast.rooms');
        excludedRows.push('forecast.rooms.variance');
        excludedRows.push('forecast.occupancy');
        excludedRows.push('forecast.occupancy.variance');
    }
    if (!canViewSellingRate) {
        excludedRows.push('sellingRate');
    }
    if (!options.showBlocks) {
        excludedRows.push('invBlock.revenue');
        excludedRows.push('invBlock.rooms');
    }
    if (!canViewPMSBalancing) {
        excludedRows.push('statistics.revenue.accommodation');
    }

    const transformedData = transformDataForGrid(data, excludedRows);
    const formattedData = formatData(transformedData, currency);
    const groupedData = Object.values(
        groupRows(formattedData, rowsGrouping, subRowsVisibility, onClickGroupingLabelCellHandler, excludedRows),
    );

    if (groupedData.length === 0) return renderNoData();

    const csvData = Object.values(transformDataForCSV(transformedData, currency, []));

    const colDefs = (cols) =>
        cols.map((c) => ({
            ...c,
            dataIndex: c.field,
            className: c.cellClass,
            title: () => (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <span className="colHeader" onClick={() => onClickDayColumnHeaderHandler(c)}>
                    {c.title}
                </span>
            ),
        }));

    return (
        <div>
            <div>
                <Grid
                    columnDefs={colDefs(getGridColumnDefinitions(options))}
                    data={groupedData}
                    focusedColId={todayColId}
                    getNodeChildDetails={getNodeChildDetails}
                    getRowClass={getRowClass}
                    frameworkComponents={frameworkComponents}
                />
            </div>
            <div className="BusinessReviewFooter">
                <CSVLink
                    className="BusinessReviewFooterLink"
                    data={csvData}
                    filename={`${startDate.format('YYYY-MM-DD')}_${endDate.format('YYYY-MM-DD')}__business-review.csv`}
                >
                    Export to CSV
                </CSVLink>
            </div>
        </div>
    );
};

export default BusinessReview;
